import React from 'react';
import { useNavigate } from 'react-router';
import { ClipboardArrowDownIcon, ClipboardArrowsClockwiseIcon, ThreeDotsIcon } from '../SVGIcons/SVGIcons';
import { dateConverter, makeFirstCharUpper, parseExactDate } from '../../helpers';

export default function OrderNotification(props) {
	const {
		notification,
		setRead,
		checkHref,
		deleteNotification,
		style,
		handleDropdowns,
		dropdownsOpen,
		rightSidebarDispatch,
	} = props || {};

	const notificationData = notification?.notificationData;

	// normalize: sometimes orderUuid/orderRef live on notificationData, sometimes under notificationData.order
	const orderData = notificationData?.order ?? notificationData ?? {};
	const orderUuid = orderData?.orderUuid;
	const orderRef = orderData?.orderRef;

	const href = orderUuid ? `/orders/${orderUuid}${orderRef ? `?orderRef=${orderRef}` : ''}` : null;

	let navigate = useNavigate();

	return (
		<>
			<div
				className={`mainRow ${notification.isRead ? 'opacity-50' : ''}`}
				onClick={() => {
					setRead(notification?.notificationUuid);

					if (href) {
						checkHref(href);
						navigate(href);
					}

					rightSidebarDispatch({ type: 'SET_SIDEBAR_OPEN', payload: false });
				}}>
				<div className={`iconCol ${style}`}>
					{style === 'sale' ? (
						<ClipboardArrowDownIcon iconClass='notificationIcon' />
					) : (
						<ClipboardArrowsClockwiseIcon iconClass='notificationIcon' />
					)}
				</div>

				{notification.notificationType === 'order.created' && (
					<div className='bodyCol'>
						<div className='colRowTop'>
							<h4 className={`notificationHeader ${style}`}>{`NEW ORDER RECEIVED`}</h4>
							<h4 className='notificationDate'>{parseExactDate(notification.createdAt)}</h4>
						</div>
						<div className='colRowBottom'>
							<p className='bodyText'>{`New order '${orderRef}' from ${notification.senderTeamName}`}</p>
							<ThreeDotsIcon
								iconClass='notificationSubIcon'
								onClick={(e) => {
									e.stopPropagation();
									handleDropdowns(notification?.notificationUuid);
								}}
							/>
						</div>
					</div>
				)}

				{notification.notificationType === 'order.updated' && (
					<div className='bodyCol'>
						<div className='colRowTop'>
							<h4 className={`notificationHeader ${style}`}>{`ORDER UPDATED`}</h4>
							<h4 className='notificationDate'>
								{notification.createdAt ? parseExactDate(notification?.createdAt) : ''}
							</h4>
						</div>
						<div className='colRowBottom'>
							<p className='bodyText'>
								{notificationData?.updatedFields?.hold?.confirmationRequired?.isDone === true ? (
									<>
										{`${notification.senderTeamName} has marked`}{' '}
										<span className='highlight warning'>hold</span>{' '}
										{`task as done for order '${orderRef}'`}
									</>
								) : (
									<>
										{`Order '${orderRef}' ${
											notificationData?.updatedFields?.hold?.isHold
												? `was put on `
												: 'has been updated'
										}`}
										{notificationData?.updatedFields?.hold?.isHold && (
											<span className='highlight warning'>hold</span>
										)}
									</>
								)}
							</p>
							<ThreeDotsIcon
								iconClass='notificationSubIcon'
								onClick={(e) => {
									e.stopPropagation();
									handleDropdowns(notification?.notificationUuid);
								}}
							/>
						</div>
					</div>
				)}

				{notification.notificationType === 'order.hold.created' && (
					<div className='bodyCol'>
						<div className='colRowTop'>
							<h4 className={`notificationHeader ${style}`}>{`ORDER UPDATED`}</h4>
							<h4 className='notificationDate'>
								{notification.createdAt ? parseExactDate(notification?.createdAt) : ''}
							</h4>
						</div>
						<div className='colRowBottom'>
							<p className='bodyText'>
								{`Order '${orderRef}' was put on `}
								<span className='highlight warning'>hold</span>
								{orderData?.hold?.reason
									? ` because of reason: ${makeFirstCharUpper(
											orderData?.hold?.reason?.replaceAll('_', ' ')
									  )}`
									: ''}
							</p>
							<ThreeDotsIcon
								iconClass='notificationSubIcon'
								onClick={(e) => {
									e.stopPropagation();
									handleDropdowns(notification?.notificationUuid);
								}}
							/>
						</div>
					</div>
				)}

				{notification.notificationType === 'order.hold.updated' && (
					<div className='bodyCol'>
						<div className='colRowTop'>
							<h4 className={`notificationHeader ${style}`}>{`ORDER UPDATED`}</h4>
							<h4 className='notificationDate'>
								{notification.createdAt ? parseExactDate(notification?.createdAt) : ''}
							</h4>
						</div>
						<div className='colRowBottom'>
							<p className='bodyText'>
								{notificationData?.updatedFields?.hold?.confirmationRequired?.isDone === true ? (
									<>
										{`${notification.senderTeamName} has marked`}{' '}
										<span className='highlight warning'>hold</span>{' '}
										{`task as done for order '${orderRef}'`}
									</>
								) : (
									<>
										{notificationData?.updatedFields?.hold?.holdEndDate ? (
											<>
												{`The `}
												<span className='highlight warning'>hold</span>
												{` was removed from order '${orderRef}'`}
											</>
										) : (
											<></>
										)}
									</>
								)}
							</p>
							<ThreeDotsIcon
								iconClass='notificationSubIcon'
								onClick={(e) => {
									e.stopPropagation();
									handleDropdowns(notification?.notificationUuid);
								}}
							/>
						</div>
					</div>
				)}

				{notification.notificationType === 'order.priceSuggested' && (
					<div className='bodyCol'>
						<div className='colRowTop'>
							<h4 className={`notificationHeader ${style}`}>{`PRICE SUGGESTED`}</h4>
							<h4 className='notificationDate'>{parseExactDate(notification.createdAt)}</h4>
						</div>
						<div className='colRowBottom'>
							<p className='bodyText'>{`Price suggestion from '${notification.senderTeamName}' regarding order '${orderRef}' needs review`}</p>
							<ThreeDotsIcon
								iconClass='notificationSubIcon'
								onClick={(e) => {
									e.stopPropagation();
									handleDropdowns(notification?.notificationUuid);
								}}
							/>
						</div>
					</div>
				)}
			</div>

			{dropdownsOpen && dropdownsOpen[notification?.notificationUuid] && (
				<div className='notificationDropdown'>
					<p
						onClick={() => {
							deleteNotification(notification);
						}}>
						Delete
					</p>
					<p
						onClick={() => {
							setRead(notification?.notificationUuid);
						}}>
						Mark as read
					</p>
				</div>
			)}
		</>
	);
}
